import { useEffect, useState, type ReactNode } from 'react';
import type { T } from './i18n';
import { useCan } from './access';

/** A settings section: title and a short explanation on the left, the fields on the right. */
export const Section = ({ title, description, children }: { title: string; description?: string; children: ReactNode }) => (
  <section className="section">
    <div className="section-head">
      <h2>{title}</h2>
      {description && <p>{description}</p>}
    </div>
    <div className="section-body">{children}</div>
  </section>
);

export const Field = ({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) => (
  <label className="form-field">
    <span className="label">{label}</span>
    {children}
    {hint && <span className="hint">{hint}</span>}
  </label>
);

/**
 * Edit state for a settings tab: a working copy, whether it differs from what was loaded, and reset.
 * One save per tab (the save bar), instead of a button per card.
 */
export const useDraft = <X,>(initial: X) => {
  const [value, setValue] = useState<X>(initial);
  const [base, setBase] = useState<X>(initial);
  const dirty = JSON.stringify(value) !== JSON.stringify(base);
  return { value, set: setValue, dirty, reset: () => setValue(base), commit: () => setBase(value) };
};

/** Appears when something changed: Save / Discard. Briefly confirms after saving. */
export const SaveBar = ({ t, dirty, saving, error, onSave, onReset, need }: { t: T; dirty: boolean; saving: boolean; error?: string | null; onSave: () => void; onReset: () => void; need?: string }) => {
  // ADR-071: without the permission the bar says so instead of offering a Save that would be refused.
  const allowed = useCan()(need);
  const [justSaved, setJustSaved] = useState(false);
  const [wasSaving, setWasSaving] = useState(false);
  useEffect(() => {
    if (wasSaving && !saving && !error && !dirty) {
      setJustSaved(true);
      const id = setTimeout(() => setJustSaved(false), 2500);
      return () => clearTimeout(id);
    }
    setWasSaving(saving);
    return undefined;
  }, [saving, error, dirty, wasSaving]);
  if (justSaved && !dirty) return <div className="savebar saved" role="status">{t('settings.saved')}</div>;
  if (!dirty && !error) return null;
  return (
    <div className="savebar" role="region" aria-label={t('settings.unsaved')}>
      <span className="savebar-note">{error ?? (allowed ? t('settings.unsaved') : t('access.readOnly', { name: t(`access.perm.${need}`) }))}</span>
      <span className="row">
        <button type="button" className="ghost" disabled={saving} onClick={onReset}>{t('settings.discard')}</button>
        <button type="button" className="primary" disabled={saving || !dirty || !allowed} onClick={onSave}>{saving ? t('settings.saving') : t('settings.save')}</button>
      </span>
    </div>
  );
};

/** A quiet delete that asks once more before acting. */
export const DangerButton = ({ t, label, onConfirm, need }: { t: T; label: string; onConfirm: () => void; need?: string }) => {
  const allowed = useCan()(need);
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (!asking) return undefined;
    const id = setTimeout(() => setAsking(false), 3000);
    return () => clearTimeout(id);
  }, [asking]);
  // ADR-071: a delete the person may not do is not offered at all.
  if (!allowed) return null;
  return (
    <button type="button" className={`danger ${asking ? 'confirm' : ''}`} onClick={() => (asking ? (setAsking(false), onConfirm()) : setAsking(true))}>
      {asking ? t('common.sure') : label}
    </button>
  );
};

/** ADR-071: shows its children only to a person with the permission (everyone while it is not known yet). */
export const Gate = ({ need, children }: { need: string; children?: ReactNode }) => (useCan()(need) ? <>{children}</> : null);

/** ADR-071: one quiet line on a page or panel the person can see but not change (nothing when they can). */
export const ReadOnlyNote = ({ t, need }: { t: T; need: string }) =>
  useCan()(need) ? null : <div className="banner info small" role="note">{t('access.readOnly', { name: t(`access.perm.${need}`) })}</div>;

/** ADR-071: a page opened by its address without the permission it needs. */
export const NoAccess = ({ t, need }: { t: T; need: string }) => (
  <div className="banner warn" role="alert">{t('access.noPage', { name: t(`access.perm.${need}`) })}</div>
);
